import type { WorkflowTrigger } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, cn, EmptyState, Icon } from "@sagot/ui";
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";
import type { PathDef } from "../../types";
import { useServiceDeskState } from "../../service/service-desk-context";
import { useDefaultWorkflow } from "../system/default-context";
import type { AddStepEntry } from "./add-step-catalog";
import { optionIdOf } from "./add-step-item";
import { buildAddStepModel, type SectionId } from "./add-step-model";
import { AddStepSection } from "./add-step-section";
import { SystemLockNote } from "./system-lock-note";
import { useCollapsedSections } from "./use-collapsed-sections";

const S = "engage.workflows.addStep";

interface NavOption {
  id: string;
  entry: AddStepEntry;
  section: SectionId;
}

export interface AddStepPanelProps {
  trigger: WorkflowTrigger;
  plan: string | undefined;
  pathId: string;
  path: PathDef;
  /** Classes that cap the height of the panel (the popover and the bottom sheet have different limits). */
  className?: string;
  onChoose: (entry: AddStepEntry, section: SectionId) => void;
}

const isPrintable = (event: KeyboardEvent): boolean => event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;

/**
 * The inside of the Add-step menu: a search box (a combobox) over sections of steps (listboxes). Focus stays in the search
 * box; Up / Down / Home / End move the highlighted step (`aria-activedescendant`) and Enter adds it. Tab goes on to the section
 * headings and the "View more" buttons.
 */
export function AddStepPanel({ trigger, plan, pathId, path, className, onChoose }: AddStepPanelProps) {
  const { t } = useTranslation();
  const panelId = useId();
  const input = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const { collapsed, toggle } = useCollapsedSections();
  const byKeyboard = useRef(false);
  const defaults = useDefaultWorkflow();
  const serviceDesk = useServiceDeskState().state;
  // A step a default cannot hold is listed but locked; picking it says why and where it can be used instead.
  const [explained, setExplained] = useState<AddStepEntry | null>(null);
  const model = useMemo(() => buildAddStepModel({ trigger, plan, pathId, path, query, t, defaults, serviceDesk }), [trigger, plan, pathId, path, query, t, defaults, serviceDesk]);

  const isOpen = (id: SectionId) => model.searching || !collapsed.has(id);
  const options: NavOption[] = model.sections.flatMap((section) => {
    if (!isOpen(section.id)) return [];
    const shown = expanded.has(section.id) && !model.searching ? [...section.visible, ...section.rest] : section.visible;
    return shown.map((entry) => ({ id: optionIdOf(panelId, section.id, entry.id), entry, section: section.id }));
  });
  const fallback = (options.find((option) => !option.entry.locked) ?? options[0])?.id ?? null;
  const activeId = options.some((option) => option.id === picked) ? picked : fallback;

  useEffect(() => { input.current?.focus(); }, []);
  useEffect(() => {
    if (!byKeyboard.current || !activeId) return;
    byKeyboard.current = false;
    document.getElementById(activeId)?.scrollIntoView({ block: "nearest" });
  }, [activeId]);

  const moveTo = (id: string | undefined) => {
    if (!id) return;
    byKeyboard.current = true;
    setPicked(id);
  };
  const step = (delta: number) => {
    if (options.length === 0) return;
    const at = options.findIndex((option) => option.id === activeId);
    moveTo(options[(at + delta + options.length) % options.length]?.id);
  };
  const choose = (entry: AddStepEntry | undefined, section: SectionId | undefined) => {
    if (entry?.triggerLock === "system") setExplained(entry);
    if (entry && section && !entry.locked) onChoose(entry, section);
  };
  const navigate = (event: KeyboardEvent): boolean => {
    switch (event.key) {
      case "ArrowDown":
        step(1);
        return true;
      case "ArrowUp":
        step(-1);
        return true;
      case "Home":
        moveTo(options[0]?.id);
        return true;
      case "End":
        moveTo(options.at(-1)?.id);
        return true;
      case "Enter": {
        if (event.nativeEvent.isComposing) return false;
        const active = options.find((option) => option.id === activeId);
        choose(active?.entry, active?.section);
        return true;
      }
      default:
        return false;
    }
  };
  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (navigate(event)) event.preventDefault();
  };
  /** From a heading or a "View more" button the arrows go back to the search box, and typing goes into it. */
  const onPanelKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target === input.current || event.defaultPrevented) return;
    const arrow = event.key === "ArrowDown" || event.key === "ArrowUp";
    if (!arrow && !isPrintable(event)) return;
    if (event.key === " ") return;
    input.current?.focus();
    if (arrow && navigate(event)) event.preventDefault();
  };
  const toggleExpanded = (id: string) => {
    const next = new Set(expanded);
    if (!next.delete(id)) next.add(id);
    setExpanded(next);
  };
  const clearSearch = () => {
    setQuery("");
    setPicked(null);
    input.current?.focus();
  };

  const listIds = model.sections
    .filter((section) => isOpen(section.id))
    .flatMap((section) => [`${panelId}-${section.id}-list`, ...(expanded.has(section.id) && !model.searching && section.rest.length > 0 ? [`${panelId}-${section.id}-more`] : [])]);
  const resultsText = t(`${S}.results`, { count: options.length });
  return (
    // The keydown handler only hands arrow keys and typing back to the search box; every control inside has its own role.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- delegated keyboard handling, not an interactive element
    <div className={cn("flex min-h-0 flex-col", className)} onKeyDown={onPanelKeyDown}>
      <div className="flex shrink-0 items-center gap-2 border-b border-line px-3 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary">
        <Icon name="search" className="text-fg-subtle" />
        <input
          ref={input}
          type="text"
          role="combobox"
          aria-label={t(`${S}.searchLabel`)}
          aria-expanded="true"
          aria-autocomplete="list"
          aria-controls={listIds.length > 0 ? listIds.join(" ") : undefined}
          aria-activedescendant={activeId ?? undefined}
          aria-describedby={`${panelId}-results`}
          autoComplete="off"
          spellCheck={false}
          value={query}
          placeholder={t(`${S}.searchPlaceholder`)}
          className="h-11 min-w-0 flex-1 bg-transparent text-sm text-fg outline-none placeholder:text-fg-subtle"
          onChange={(event) => {
            setQuery(event.target.value);
            setPicked(null);
          }}
          onKeyDown={onInputKeyDown}
        />
      </div>
      <div id={`${panelId}-results`} role="status" aria-live="polite" className="sr-only">
        {resultsText}
      </div>
      <div className="scrollbar-visible min-h-0 flex-1 overflow-y-auto p-1">
        {explained ? <SystemLockNote step={explained.label} canDuplicate={defaults?.canDuplicate !== false} /> : null}
        {model.replace ? (
          <p className="px-3 py-2 text-xs text-fg-muted">{t(`${S}.replaceNotice`, { step: model.replace.label })}</p>
        ) : null}
        {model.sections.map((section) => (
          <AddStepSection
            key={section.id}
            section={section}
            panelId={panelId}
            searching={model.searching}
            open={isOpen(section.id)}
            expanded={expanded.has(section.id)}
            activeId={activeId}
            onToggle={() => { toggle(section.id); }}
            onToggleExpanded={() => { toggleExpanded(section.id); }}
            onActivate={(optionId) => {
              byKeyboard.current = false;
              setPicked(optionId);
            }}
            onChoose={(entry) => { choose(entry, section.id); }}
          />
        ))}
        {model.searching && options.length === 0 ? (
          <EmptyState
            icon="search"
            headingLevel={3}
            className="py-6"
            title={t(`${S}.empty.title`, { query: query.trim() })}
            description={t(`${S}.empty.body`)}
            action={
              <Button type="button" variant="secondary" size="sm" onClick={clearSearch}>
                {t(`${S}.empty.clear`)}
              </Button>
            }
          />
        ) : null}
      </div>
    </div>
  );
}
